import { AvailabilityTypes, LocationTypes } from 'common';
import { useInjection } from 'inversify-react';
import { useCallback } from 'react';

import { WizardStep } from 'domain/entities/WizardStep';
import { AsyncHookResult, InjectableHook, useHookInjection } from 'domain/hooks';

// RTK Query lazy trigger function type
type LazyQueryTrigger<TArgs, TResult> = (args: TArgs) => {
  unwrap(): Promise<TResult>;
  abort(): void;
  unsubscribe(): void;
  requestId: string;
};

import {
  GetSimulatorLocationsAdapter,
  IGetSimulatorLocationsHook,
} from './useCaseGetSimulatorLocations';
import {
  BookingWizardNavigationHook,
  IBookingWizardNavigationHook,
} from './useCaseNavigateBookingWizard';

export enum GetInstructorsError {
  Unknown = 'unknown',
  Unauthorized = 'unauthorized',
}
export const GetInstructorsWebAdapter = Symbol('GetInstructorsWebAdapter');

export type IGetInstructorsWebAdapter = InjectableHook<
  AsyncHookResult<Array<AvailabilityTypes.StaffAppointmentAvailability>, GetInstructorsError> & {
    saveSelectedInstructorToStore: (staffId: number) => void;
    setFlowTypeToLesson: () => void;
    flowType: string | undefined;
    selectFirstLessonFlow: () => void;
    clearPartySize: () => void;
    selectedInstructor: Partial<AvailabilityTypes.LessonAppointmentAvailability> | undefined;
    getInstructors: LazyQueryTrigger<
      {
        locationId: string;
        startDateTime?: string;
        endDateTime?: string;
        email?: string;
        checkAvailability?: boolean;
      },
      AvailabilityTypes.StaffAppointmentAvailability[]
    >;
    handleSaveSelectedLocationToStore: (selectedLocation: LocationTypes.SimulatorLocation) => void;
    handleSaveReservationDateToStore: (reservationDate: Date) => void;
    userEmail: string;
    getInstructorsByDate: LazyQueryTrigger<
      {
        locationId: string;
        startDateTime?: string;
        endDateTime?: string;
        email?: string;
        includeLastBooked?: boolean;
      },
      AvailabilityTypes.StaffAppointmentAvailability[]
    >;
    availableInstructorsOnDate: AvailabilityTypes.StaffAppointmentAvailability[] | undefined;
    areAvailableInstructorsOnDateLoading: boolean;
    availableInstructorsOnDateError: GetInstructorsError | undefined;
    areAvailableInstructorsOnDateFetching: boolean;
  }
>;

const useCaseSelectInstructorWeb = (currentStep?: WizardStep) => {
  const adapter = useInjection<IGetInstructorsWebAdapter>(GetInstructorsWebAdapter);

  // Always call the hook, even if you don't need it right away
  const bookingWizardNavigation = useHookInjection<IBookingWizardNavigationHook>(
    BookingWizardNavigationHook,
  );

  const {
    error,
    result,
    inProgress,
    selectedInstructor,
    saveSelectedInstructorToStore,
    setFlowTypeToLesson,
    selectFirstLessonFlow,
    clearPartySize,
    getInstructors,
    handleSaveSelectedLocationToStore,
    handleSaveReservationDateToStore,
    userEmail,
    getInstructorsByDate,
    availableInstructorsOnDate,
    areAvailableInstructorsOnDateLoading,
    availableInstructorsOnDateError,
    areAvailableInstructorsOnDateFetching,
  } = adapter();

  const handleNextStep = (staffId: number | null, isFirstTimeLesson?: boolean) => {
    clearPartySize();
    if (isFirstTimeLesson) {
      selectFirstLessonFlow();
    } else if (staffId) {
      saveSelectedInstructorToStore(staffId);
    }
    if (bookingWizardNavigation && currentStep) {
      bookingWizardNavigation.redirectToNextStep(currentStep);
    }
  };

  const getLocationsHook = useHookInjection<IGetSimulatorLocationsHook>(
    GetSimulatorLocationsAdapter,
  );
  const locations = getLocationsHook.result;

  const preselectLocation = useCallback(
    (locationSlug: string | null) => {
      const preselectedLocation = locations?.find((location) => location.urlSlug === locationSlug);
      if (preselectedLocation) {
        handleSaveSelectedLocationToStore(preselectedLocation);
      }
    },

    [locations, handleSaveSelectedLocationToStore],
  );

  const preselectInstructor = useCallback(
    (instructorSlug: string | null): number | undefined => {
      if (instructorSlug && result && result.length > 0) {
        const preselectedInstructor = result?.find((instructor) => {
          const [firstName, lastName] = instructorSlug.split('-');
          return (
            instructor.staffFirstName.toLocaleLowerCase() === firstName.toLocaleLowerCase() &&
            instructor.staffLastName.toLocaleLowerCase() === lastName.toLocaleLowerCase()
          );
        });
        if (preselectedInstructor) {
          saveSelectedInstructorToStore(preselectedInstructor.staffId);
          return preselectedInstructor.staffId;
        }
      }
    },
    [result, saveSelectedInstructorToStore],
  );

  const preselectReservationDate = (reservationDate: string | null) => {
    if (reservationDate) {
      handleSaveReservationDateToStore(new Date(reservationDate));
    }
  };

  return {
    inProgress,
    error,
    result,
    getInstructors,
    nextStep: handleNextStep,
    prevStep: () => {
      if (bookingWizardNavigation && currentStep) {
        bookingWizardNavigation.redirectToPrevStep(currentStep);
      }
    },
    preselectLocation,
    preselectInstructor,
    preselectReservationDate,
    selectedInstructor,
    userEmail,
    getInstructorsByDate,
    availableInstructorsOnDate,
    areAvailableInstructorsOnDateLoading,
    availableInstructorsOnDateError,
    areAvailableInstructorsOnDateFetching,
  };
};

export default useCaseSelectInstructorWeb;
